import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './Login.css';
import { api } from './services/apiClient';
import Swal from 'sweetalert2';
import { useLang } from './i18n/context';
import BrandMark from './components/BrandMark';

const TURNSTILE_SITE_KEY = '0x4AAAAAADdbvGQnzi_o_JqQ';

function Register() {
  const navigate = useNavigate();
  const { t } = useLang();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [emailError, setEmailError] = useState('');
  const [email, setEmail] = useState('');
  const [emailCode, setEmailCode] = useState('');
  const [sendingCode, setSendingCode] = useState(false);
  const [codeCooldown, setCodeCooldown] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState('');
  const turnstileRef = useRef(null);
  const turnstileWidgetId = useRef(null);

  const validateEmail = (value) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value);

  const handleEmailBlur = () => {
    if (email && !validateEmail(email)) setEmailError(t('register.validEmail'));
    else setEmailError('');
  };

  useEffect(() => {
    if (codeCooldown <= 0) return undefined;
    const timer = window.setTimeout(() => setCodeCooldown(codeCooldown - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [codeCooldown]);

  useEffect(() => {
    let cancelled = false;
    let timer;

    const renderTurnstile = () => {
      if (cancelled || !turnstileRef.current || !window.turnstile?.render) return false;
      if (turnstileWidgetId.current !== null) return true;

      turnstileWidgetId.current = window.turnstile.render(turnstileRef.current, {
        sitekey: TURNSTILE_SITE_KEY,
        callback: (token) => setTurnstileToken(token),
        'expired-callback': () => setTurnstileToken(''),
        'error-callback': () => setTurnstileToken(''),
      });
      return true;
    };

    if (!renderTurnstile()) {
      timer = window.setInterval(() => {
        if (renderTurnstile()) window.clearInterval(timer);
      }, 300);
    }

    return () => {
      cancelled = true;
      if (timer) window.clearInterval(timer);
      if (turnstileWidgetId.current !== null && window.turnstile?.remove) {
        window.turnstile.remove(turnstileWidgetId.current);
      }
      turnstileWidgetId.current = null;
    };
  }, []);

  const handleSendEmailCode = async () => {
    if (!email) {
      Swal.fire({ title: '请先输入邮箱', icon: 'warning', confirmButtonText: t('register.confirm') });
      return;
    }
    if (!validateEmail(email)) {
      Swal.fire({ title: t('register.invalidEmail'), icon: 'warning', confirmButtonText: t('register.confirm') });
      return;
    }

    try {
      setSendingCode(true);
      await api.sendEmailCode({ email });
      setCodeCooldown(60);
      Swal.fire({
        icon: 'success',
        title: '验证码已发送',
        text: '请查看邮箱并在 15 分钟内完成注册',
        background: '#1e222d',
        color: '#d1d4dc',
        confirmButtonColor: '#bfa14a',
        confirmButtonText: t('register.confirm'),
      });
    } catch (err) {
      console.error('发送邮箱验证码失败：', err);
      Swal.fire({
        icon: 'error',
        title: '验证码发送失败',
        text: err?.message || t('register.retryLater'),
        background: '#1e222d',
        color: '#d1d4dc',
        confirmButtonColor: '#ef5350',
        confirmButtonText: t('register.confirm'),
      });
    } finally {
      setSendingCode(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !emailCode || !password || !confirm) {
      Swal.fire({ title: t('register.fillAll'), icon: 'warning', confirmButtonText: t('register.confirm') });
      return;
    }
    if (!validateEmail(email)) {
      Swal.fire({ title: t('register.invalidEmail'), icon: 'warning', confirmButtonText: t('register.confirm') });
      return;
    }
    if (password.length < 8) {
      Swal.fire({ title: t('register.passwordTooShort'), icon: 'warning', confirmButtonText: t('register.confirm') });
      return;
    }
    if (password !== confirm) {
      Swal.fire({ title: t('register.passwordMismatch'), icon: 'warning', confirmButtonText: t('register.confirm') });
      return;
    }

    if (!turnstileToken) {
      Swal.fire({
        title: '请先完成人机验证',
        icon: 'warning',
        background: '#1e222d',
        color: '#d1d4dc',
        confirmButtonColor: '#bfa14a',
        confirmButtonText: t('register.confirm'),
      });
      return;
    }

    try {
      setSubmitting(true);
      await api.register({
        email,
        emailCode,
        password,
        inviteCode: inviteCode || null,
        turnstileToken,
      });

      Swal.fire({
        icon: 'success',
        title: t('register.success'),
        text: t('register.successMsg'),
        background: '#1e222d',
        color: '#d1d4dc',
        confirmButtonColor: '#bfa14a',
        confirmButtonText: t('register.goToLogin'),
      }).then(() => navigate('/login'));
    } catch (err) {
      console.error('注册失败：', err);
      if (turnstileWidgetId.current !== null) window.turnstile?.reset?.(turnstileWidgetId.current);
      setTurnstileToken('');
      Swal.fire({
        icon: 'error',
        title: t('register.failed'),
        text: err?.message || t('register.retryLater'),
        background: '#1e222d',
        color: '#d1d4dc',
        confirmButtonColor: '#ef5350',
        confirmButtonText: t('register.confirm'),
      });
    } finally {
      setSubmitting(false);
    }
  };

  const codeButtonText = codeCooldown > 0 ? `${codeCooldown}s` : (sendingCode ? '发送中' : '获取验证码');

  return (
    <div className="auth-form">
      <div className="auth-logo">
        <BrandMark className="auth-logo-mark" />
        GHOST
      </div>

      <h1 className="auth-title">{t('register.title')}</h1>
      <p className="auth-subtitle">Web3 Community</p>

      <form className="auth-card" onSubmit={handleSubmit}>
        <input type="text" placeholder={t('register.emailPlaceholder')} value={email} onChange={e => setEmail(e.target.value)} onBlur={handleEmailBlur} className={emailError ? 'input-error' : ''} />
        {emailError && <div className="input-error-text">{emailError}</div>}
        <div className="auth-code-row">
          <input type="text" placeholder="邮箱验证码" value={emailCode} onChange={e => setEmailCode(e.target.value.replace(/\D/g, '').slice(0, 6))} />
          <button type="button" className="auth-code-button" onClick={handleSendEmailCode} disabled={sendingCode || codeCooldown > 0}>
            {codeButtonText}
          </button>
        </div>
        <input type="password" placeholder={t('register.passwordPlaceholder')} value={password} onChange={e => setPassword(e.target.value)} />
        <input type="password" placeholder={t('register.confirmPasswordPlaceholder')} value={confirm} onChange={e => setConfirm(e.target.value)} />
        <div className="auth-divider" />
        <input type="text" placeholder={t('register.inviteCodePlaceholder')} value={inviteCode} onChange={e => setInviteCode(e.target.value)} />
        <div ref={turnstileRef} style={{ marginTop: '0.5rem', minHeight: 65 }} />
        <button type="submit" className="auth-submit" disabled={submitting}>
          {submitting ? t('register.submitting') : t('register.submit')}
        </button>
      </form>

      <div className="auth-footer">
        <button className="auth-link" onClick={() => navigate('/login')}>{t('register.backToLogin')}</button>
      </div>
    </div>
  );
}

export default Register;
